import React, { useState, useEffect, useCallback } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, List, ListItem, ListItemText, IconButton } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../data/categoriesApi';
import { FetchWithAuth } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface Category {
    id: number;
    name: string;
}

interface CategoryManagerProps {
    fetchWithAuth: FetchWithAuth;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ fetchWithAuth }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [open, setOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
    const [name, setName] = useState('');
    const { t } = useLanguage();

    const fetchCategories = useCallback(async () => {
        const data = await getCategories(fetchWithAuth);
        setCategories(data);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleOpen = (category: Category | null = null) => {
        setCurrentCategory(category);
        setName(category ? category.name : '');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentCategory(null);
        setName('');
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('category.nameRequired'));
            return;
        }
        if (currentCategory) {
            await updateCategory(fetchWithAuth, currentCategory.id, { name });
        } else {
            await createCategory(fetchWithAuth, { name });
        }
        fetchCategories();
        handleClose();
    };

    const handleDelete = async (id: number) => {
        try {
            const res = await deleteCategory(fetchWithAuth, id);
            if (res.ok) {
                fetchCategories();
            } else {
                alert(t('category.deleteError'));
            }
        } catch (error: any) {
            alert(t('category.deleteError'));
        }
    };

    return (
        <div>
            <List>
                {categories.length > 0 && (
                    <ListItem
                        sx={{
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: '#f0f4ff',
                        }}
                    >
                        <ListItemText
                            primary={t('category.subtitle')}
                            primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.main' }}
                        />
                    </ListItem>
                )}
                {categories.map((category) => (
                    <ListItem
                        key={category.id}
                        sx={{
                            borderBottom: '1px solid #e0e0e0',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                        secondaryAction={
                            <>
                                <IconButton onClick={() => handleOpen(category)} edge="end" aria-label="edit">
                                    <Edit />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(category.id)} edge="end" aria-label="delete">
                                    <Delete />
                                </IconButton>
                            </>
                        }
                    >
                        <ListItemText primary={category.name} />
                    </ListItem>
                ))}
            </List>
            <Button variant="contained" onClick={() => handleOpen()}>{t('category.new')}</Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{currentCategory ? t('category.edit') : t('category.new')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('category.name')}
                        type="text"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('category.cancel')}</Button>
                    <Button onClick={handleSave}>{t('category.save')}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CategoryManager;
